import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { classroomActions } from "../slices/classroom";
import { getErrorMessage } from "../../utils/errorUtils";
import { ClassroomRequestDto } from "../../shared/dtos/classroom/ClassroomRequestDto";
import { ClassroomResponseDto } from "../../shared/dtos/classroom/ClassroomResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createClassroom =
  (values: ClassroomRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(classroomActions.setLoadingCreate());

    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<ClassroomResponseDto>
      >("/classrooms", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Classroom created successfully!");

        dispatch(classroomActions.createSuccess(data.data));
        dispatch(
          classroomActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(classroomActions.setErrorCreate(message));
    }
  };

export const getClassrooms =
  (params?: { branchId?: string }) => async (dispatch: AppDispatch) => {
    dispatch(classroomActions.setLoadingFetchAll());

    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<ClassroomResponseDto[]>
      >("/classrooms", {
        params,
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(classroomActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(classroomActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getClassroomById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(classroomActions.setLoadingFetchOne());

    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<ClassroomResponseDto>
      >(`/classrooms/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(classroomActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(classroomActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateClassroom =
  (payload: { id: string; values: ClassroomRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(classroomActions.setLoadingUpdate());

    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<ClassroomResponseDto>
      >(`/classrooms/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Classroom updated successfully!");

        dispatch(classroomActions.updateSuccess(data.data));
        dispatch(
          classroomActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(classroomActions.setErrorUpdate(message));
    }
  };

export const deleteClassroom =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(classroomActions.setLoadingDelete());

    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/classrooms/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Classroom deleted successfully!");

        dispatch(classroomActions.deleteSuccess(id));
        dispatch(
          classroomActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(classroomActions.setErrorDelete(message));
    }
  };

export const resetClassroomServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(classroomActions.setServerStatus(null));
};
